import { useState, useEffect } from 'react'
import { api } from '../utils/api'

interface SiteUser {
  id: string
  email: string
  name: string | null
  createdAt: string
}

interface Props {
  siteId: string | null
  onClose: () => void
}

export function SiteUsersModal({ siteId, onClose }: Props) {
  const [users, setUsers] = useState<SiteUser[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    if (!siteId) { setLoading(false); return }
    api.get<SiteUser[]>(`/sites/${siteId}/site-users`)
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false))
  }, [siteId])

  async function handleDelete(userId: string) {
    if (!siteId) return
    setDeleting(userId)
    try {
      await api.delete(`/sites/${siteId}/site-users/${userId}`)
      setUsers(prev => prev.filter(u => u.id !== userId))
    } catch { /* noop */ } finally { setDeleting(null) }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '16px', width: '640px', maxWidth: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>🔐 Пользователи сайта</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>Посетители, зарегистрировавшиеся через блок «Вход / Регистрация»</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Загрузка...</div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>👤</div>
              <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)' }}>Нет зарегистрированных пользователей</div>
              <div style={{ fontSize: '12px' }}>Добавьте блок «Вход / Регистрация» на сайт, чтобы собирать аудиторию</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Имя', 'Email', 'Дата регистрации', ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 12px', color: 'var(--text-primary)', fontWeight: 500 }}>{user.name ?? '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{user.email}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{new Date(user.createdAt).toLocaleDateString('ru', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => handleDelete(user.id)}
                        disabled={deleting === user.id}
                        style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', opacity: deleting === user.id ? 0.5 : 1 }}
                      >
                        {deleting === user.id ? '...' : 'Удалить'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', fontSize: '11px', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{users.length} пользователей</span>
          <button onClick={onClose} style={{ padding: '6px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)' }}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}
